import * as React from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InfoNote from 'components/info-note/InfoNote';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { approveCcmCampaign } from 'modules/campaigns/components/ccm/redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { getDisplayDate } from 'utils/date';

import './ccm-campaign-info-note.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./ccm-campaign-info-note.i18n.json'));

import type { Campaign } from 'types';

type Props = {
  campaign: Campaign,
  isModerator: boolean
};

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'ccm_campaign_info_note';

const CcmCampaignInfoNote: React.FC<Props> = ({
  campaign,
  isModerator
}): JSX.Element => {
  const dispatch = useDispatch();

  const handleApproval = (): void => {
    const innerContent = __t(`${MSG_KEY}.modals.approval.inner_content`, {
      date: getDisplayDate(campaign.startDate)
    });

    dispatch(showConfirmModal({
      title: __t(`${MSG_KEY}.modals.approval.title`),
      message: (
        <div className="ccm-campaign-info-note__modal-message">
          {__t(`${MSG_KEY}.modals.approval.message`)}
        </div>
      ),
      innerContent: (
        <div
          className="ccm-campaign-info-note__modal-inner-content"
          dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(innerContent) }}
        />
      ),
      confirmButtonColor: 'primary',
      confirmButtonLabel: __t(`${COMMON_MSG_KEY}.action_button.approve`),
      onConfirm: () => {
        dispatch(hideConfirmModal());
        dispatch(approveCcmCampaign(campaign.id));
      },
      onCancel: () => {
        dispatch(hideConfirmModal());
      }
    }));
  };

  const renderModeratorNote = (): React.ReactNode => (
    <InfoNote
      type="warning"
      size="normal"
      className="ccm-campaign-info-note__moderator"
    >
      <div className="ccm-campaign-info-note__body--moderator">
        <div>
          {__t(`${MSG_KEY}.messages.pending_approval_moderator`)}
        </div>
        <ActionButton
          color="primary"
          onClick={handleApproval}
        >
          {__t(`${COMMON_MSG_KEY}.action_button.approve`)}
        </ActionButton>
      </div>
    </InfoNote>
  );

  const renderBrandNote = (): React.ReactNode => (
    <InfoNote
      type="warning"
      size="normal"
    >
      <div className="ccm-campaign-info-note__body--brand">
        {__t(`${MSG_KEY}.messages.pending_approval_brand`)}
      </div>
    </InfoNote>
  );

  const containerClasses = classnames('ccm-campaign-info-note', {
    'ccm-campaign-info-note--moderator': isModerator,
    'ccm-campaign-info-note--brand': !isModerator
  });

  return (
    <div className={containerClasses}>
      {isModerator && campaign.state === 'draft' && renderModeratorNote()}
      {!isModerator && campaign.state === 'draft' && renderBrandNote()}
    </div>
  );
};

export default CcmCampaignInfoNote;
